/** @jsxImportSource jsx-dom */

import { CountryLanguageResponse } from "../../../../types";
import { buildIcons, loadPaymentMethods } from "../../../payment-icons";
import { Icon } from "../../Icon";
import { SuccessButton } from "../../Buttons/SuccessButton";
import { LocaleSelect } from "./LocaleSelect";
import { RoundFlag, setFlagCountry } from "./RoundFlag";
import { createPickerLoading } from "./loading";
import {
  LocaleSelection,
  findCountry,
  formatCurrency,
  labelClass,
  translate,
} from "./shared";

type LocalePickerModalContentProps = {
  data: CountryLanguageResponse;
  selection: LocaleSelection;
  onUpdate: (selection: LocaleSelection) => void;
  onLanguageChange: (language: string, form: LocalePickerFormControls) => void;
};

export type LocalePickerFormControls = {
  // Shows the form's loading state while the new language loads, and returns a function to end it
  startLoading: () => () => void;
  // The shopper's choices as they are now, which may have changed since the language was picked
  getSelection: () => LocaleSelection;
};

export const LocalePickerModalContent = ({
  data,
  selection,
  onUpdate,
  onLanguageChange,
}: LocalePickerModalContentProps) => {
  const current = { ...selection };
  const t = (key: string) => translate(key, selection.language);

  // jsx-dom builds real elements, so keep hold of the ones that change rather than looking them up later
  const flag = (<RoundFlag country={selection.country} />) as HTMLElement;
  const currency = (
    <p
      class="tw:m-0"
      aria-labelledby="locale-picker-currency-label"
      aria-live="polite"
      data-testid="locale-picker-currency"
    >
      {formatCurrency(findCountry(data, selection.country))}
    </p>
  ) as HTMLElement;
  const paymentIcons = (
    <div data-testid="locale-picker-payment-methods">
      <div class="skeleton py-16"></div>
    </div>
  ) as HTMLElement;
  const updateButton = (
    <SuccessButton cssClass="tw:w-full" testId="locale-picker-update">
      {t("localepicker.update")}
    </SuccessButton>
  ) as HTMLButtonElement;
  const loadingOverlay = (
    <div
      class="tw:hidden tw:absolute tw:inset-0 tw:z-10 tw:items-center tw:justify-center tw:bg-white/70"
      role="status"
      data-testid="locale-picker-loading"
    >
      <img
        class="tw:w-8 tw:h-8"
        width="50"
        height="50"
        src="/marketplace/images/loader.svg"
        alt=""
      />
      <span class="sr-only">{t("common.loading")}</span>
    </div>
  ) as HTMLElement;

  const form = (
    <form
      class="tw:relative tw:px-4 tw:pb-4 tw:flex tw:flex-col tw:gap-4"
      onSubmit={(event: Event) => {
        event.preventDefault();
        onUpdate({ ...current });
      }}
    >
      <h2 id="locale-picker-title" class="tw:m-0">
        {t("localepicker.title")}
      </h2>
      <LocaleSelect
        id="locale-picker-language"
        label={t("localepicker.language")}
        icon={
          <Icon
            name="translate"
            width={20}
            height={20}
            // The global .icon rule sizes icons at 1.5em, which would override the 20px design size
            className="tw:size-5"
          />
        }
        options={data.languages.map((language) => ({
          value: language.language,
          label: language.localisedDisplayName,
          lang: language.language,
        }))}
        selected={selection.language}
        testId="locale-picker-language"
        onChange={(language) => {
          current.language = language;
          onLanguageChange(language, {
            startLoading,
            getSelection: () => ({ ...current }),
          });
        }}
      />
      <LocaleSelect
        id="locale-picker-country"
        label={t("localepicker.shopIn")}
        icon={flag}
        options={data.countries.map((country) => ({
          value: country.country,
          label: country.localisedDisplayName,
        }))}
        selected={selection.country}
        testId="locale-picker-country"
        onChange={(country) => {
          current.country = country;
          setFlagCountry(flag, country);
          currency.textContent = formatCurrency(findCountry(data, country));
          renderPaymentIcons(country);
        }}
      />
      <div>
        <p class={labelClass} id="locale-picker-currency-label">
          {t("localepicker.currency")}
        </p>
        {currency}
      </div>
      <div class="tw:border tw:border-fruugo-border-control tw:rounded-sm tw:p-4 tw:text-center">
        <p class="tw:mb-2">{t("cart.summary.payment-we-accept")}</p>
        {paymentIcons}
      </div>
      {updateButton}
      {loadingOverlay}
    </form>
  ) as HTMLFormElement;

  const startLoading = createPickerLoading({
    form,
    overlay: loadingOverlay,
    updateButton,
  });

  function renderPaymentIcons(country: string, showLoading = true) {
    const stopLoading = showLoading ? startLoading() : () => {};
    // Ignore responses for a country the shopper has already moved away from
    const isCurrentCountry = () => current.country === country;

    loadPaymentMethods(false, country)
      .then((paymentMethods) => {
        if (isCurrentCountry()) {
          paymentIcons.replaceChildren(buildIcons(paymentMethods));
        }
      })
      .catch(() => {
        if (isCurrentCountry()) paymentIcons.replaceChildren();
      })
      .finally(() => {
        if (isCurrentCountry()) stopLoading();
      });
  }

  renderPaymentIcons(selection.country, false);

  return form;
};
